import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { isEmpty, isEqual } from 'lodash';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { AreaSeries, ChartTheme, ChartType, CustomGraphColor, TitleAlign } from '../types';
import {
  CHART_AREA_OPTIONS,
  CHART_BAR_OPTIONS,
  CHART_DONUT_OPTIONS,
  CHART_LINE_OPTIONS,
  CHART_RADAR_OPTIONS,
} from '../types/chart.config';
import { getEmptyColorsChartColors } from '../types/mocked.data';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';

@customElement('gz-general-chart')
export class GzGeneralChartWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  chartTitle: string;

  @property({ type: String, attribute: true, reflect: true })
  emptyMessage = 'No data available[***]';

  @property({ type: String, attribute: true, reflect: true })
  donutTotalLabelText = 'Total[***]';

  @property({ type: String, attribute: true, reflect: true })
  titleAlign: TitleAlign = TitleAlign.LEFT;

  @property({ type: Number, attribute: true, reflect: true })
  height: number;

  @property({ type: Number, attribute: true, reflect: true })
  width: number;

  @property({ type: String, attribute: true, reflect: true })
  theme: ChartTheme = ChartTheme.LIGHT;

  @property({ type: String, attribute: true, reflect: true })
  type: ChartType = ChartType.AREA;

  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  /* Option to pass colors, and not copy the entire config */
  @property({ type: Array, attribute: true, reflect: true })
  customChartColors: CustomGraphColor;

  @property({ type: Boolean, attribute: true, reflect: true })
  displaySumInLegend: boolean = false;

  /* This property can be used only when used with type: custom */
  @property({ type: Object, attribute: true, reflect: false })
  customConfig: ApexOptions;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  series: AreaSeries[];

  @property({
    type: Array,
    attribute: true,
    reflect: true,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  categories: string[];

  @query('#chartContainer')
  chartContainer: HTMLElement;

  public chart: ApexCharts;

  private static readonly TOOLTIP_PADDING = 10;

  static get styles() {
    return styles;
  }

  static alignTooltip(mouseEvent: MouseEvent, options: any, config: any): void {
    const tooltipWidth = options.el.querySelector('.apexcharts-tooltip')?.clientWidth;
    const chartWidth = options.el.clientWidth;
    // space between beginning of the page and the chart
    const chartOffsetX = (mouseEvent.currentTarget as HTMLElement).getClientRects()[0].x;

    const positionTooltip = this.getTooltipPosition(mouseEvent.x, chartWidth, tooltipWidth, chartOffsetX);

    options.el.style.setProperty('--tooltip-left-position', positionTooltip + 'px');
    options.el.style.setProperty('--tooltip-min-width', tooltipWidth + 'px');
  }

  static getTooltipPosition(
    mousePosition: number,
    elementWidth: number,
    tooltipWidth: number,
    chartOffsetX: number
  ): number {
    if (mousePosition - chartOffsetX > elementWidth - tooltipWidth) {
      return mousePosition - tooltipWidth - chartOffsetX + this.TOOLTIP_PADDING;
    }

    return mousePosition - chartOffsetX + this.TOOLTIP_PADDING;
  }

  render(): TemplateResult {
    return html`
      <div style="width:${this.width}+px;height:${this.height}+ px;" class="chart-container" id="chartContainer"></div>
    `;
  }

  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    this.buildApexChart();
    this.setCustomWidth();
    this.setCustomHeight();
    this.setCustomGraphColor();
    this.formatLegendToDisplaySum();
    this.setTextsValues();
    this.setEmptyState();

  }

  private setTheme(): ApexOptions {
    const chartDefaultConfig = this.getDefaultConfigByType();

    return {
      chart: {
        ...chartDefaultConfig.chart,
        foreColor: this.theme === 'dark' ? '#f6f7f8' : '#373d3f',
      },
      theme: {
        mode: this.theme,
      },
      tooltip: {
        ...chartDefaultConfig.tooltip,
        theme: this.theme,
      },
    };
  }

  private formatLegendToDisplaySum(): void {
    if (this.shouldFormatLegend()) {
      return;
    }

    this.chart.updateOptions({
      legend: {
        formatter: this.legendFormatter,
      },
    });
  }

  private shouldFormatLegend(): boolean {
    const isAreaOrLineChart = this.type === ChartType.AREA || this.type === ChartType.LINE;
    return !this.displaySumInLegend || !isAreaOrLineChart;
  }

  private legendFormatter = (seriesName: string, opts: any): string | undefined => {
    const seriesData = opts.w.globals.series[opts.seriesIndex];

    if (!seriesData?.length) {
      return `${seriesName}`;
    }

    const sum = this.calculateSum(seriesData);

    return `${seriesName}: <b>${sum}</b>`;
  };

  private calculateSum(data: number[]): number {
    return data.reduce((a, b) => a + b, 0);
  }

  private setCustomGraphColor(): void {
    if (isEmpty(this.customChartColors)) {
      return;
    }

    this.chart
      .updateOptions({
        colors: this.customChartColors.lineColor,
        fill: {
          colors: this.customChartColors.fillColor,
        },
      })
      .then();
  }

  private setEmptyState(): void {
    if (this.state === WidgetStateFrontEnd.IS_EMPTY && this.type === ChartType.DONUT) {
      this.chart.updateOptions({
        series: [1],
        labels: ['-'],
        stroke: {
          width: 0,
        },
        tooltip: {
          enabled: false,
        },
        colors: getEmptyColorsChartColors().lineColor,
        fill: {
          colors: getEmptyColorsChartColors().fillColor,
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                total: {
                  formatter: function (w: any): string {
                    return '- -';
                  },
                },
              },
            },
          },
        },
        legend: {
          formatter: function (seriesName, opts): string | undefined {
            return `<div class="chart-legend">
               <gz-text fontSize="12">${seriesName}</gz-text>
              </div>
      `;
          },
        },
      }).then();
    }
  }

  private setChartTitle(): { title: { text: string; align: TitleAlign } } {
    return {
      title: {
        text: this.chartTitle,
        align: this.titleAlign,
      },
    };
  }

  private buildApexChart(): void {
    const seriesData = this.getSeriesDataByType();
    const chartDefaultConfig = {
      ...seriesData,
      ...this.getDefaultConfigByType(),
      ...this.setChartTitle(),
      ...this.setTheme(),
    };

    this.buildChart(chartDefaultConfig);
  }

  private getSeriesDataByType():
    | { series: number[] | AreaSeries[]; labels: string[]; xaxis?: undefined }
    | {
        series: number[] | AreaSeries[];
        xaxis: { categories: string[]; crosshairs: { show: boolean } };
        labels?: undefined;
      } {
    if (this.type === ChartType.DONUT) {
      return {
        series: this.series[0]['data'],
        labels: this.categories,
      };
    }

    return {
      series: this.series,
      xaxis: {
        categories: this.categories,
        crosshairs: {
          show: false,
        },
      },
    };
  }

  private getDefaultConfigByType(): any {
    switch (this.type) {
      case ChartType.AREA:
        return CHART_AREA_OPTIONS;
      case ChartType.BAR:
        return CHART_BAR_OPTIONS;
      case ChartType.LINE:
        return CHART_LINE_OPTIONS;
      case ChartType.RADAR:
        return CHART_RADAR_OPTIONS;
      case ChartType.DONUT:
        return CHART_DONUT_OPTIONS;
      case ChartType.CUSTOM:
        return this.customConfig;
      default:
        return CHART_AREA_OPTIONS;
    }
  }

  private buildChart(chartOptions: ApexOptions): void {
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new ApexCharts(this.chartContainer, chartOptions);
    this.chart.render().then();
  }

  private setCustomHeight(): void {
    if (!this.height) {
      return;
    }
    this.chart
        .updateOptions({
          chart: {
            height: this.height
          },
        })
        .then();
  }

  private setCustomWidth(): void {
    if (!this.width) {
      return;
    }
    this.chart
        .updateOptions({
          chart: {
            width: this.width
          },
        })
        .then();
  }

  private setTextsValues(): void {
    this.chart
      .updateOptions({
        noData: {
          text: this.emptyMessage,
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                total: {
                  label: this.donutTotalLabelText ? this.donutTotalLabelText : undefined,
                },
              },
            },
          },
        },
      })
      .then();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-general-chart': GzGeneralChartWebComponent;
  }
}
